import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { POLICY_CONTENT } from "./policies";

const POLICY_TABS = [
  { id: "privacy", label: "Privacy Policy", marker: "**Privacy Policy******" },
  { id: "terms", label: "Terms of Use", marker: "**Terms of Use******" },
  { id: "refund", label: "Refund Policy", marker: "**Refund Policy******" },
];

const getPolicySections = () => {
  const source = POLICY_CONTENT.value;
  return POLICY_TABS.reduce((sections, tab, index) => {
    const start = source.indexOf(tab.marker);
    const nextTab = POLICY_TABS[index + 1];
    const end = nextTab ? source.indexOf(nextTab.marker) : source.length;
    sections[tab.id] = source.slice(start, end).replace(/\n---\n?$/, "").trim();
    return sections;
  }, {});
};

const POLICY_SECTIONS = getPolicySections();

const normalizePolicyText = (text) =>
  text
    .replace(/â€œ|â€/g, "\u201c")
    .replace(/â€/g, "\u201d")
    .replace(/â€™/g, "\u2019")
    .replace(/â€“/g, "\u2013")
    .replace(/â€”/g, "\u2014")
    .replace(/â€¦/g, "\u2026")
    .replace(/Â·|·/g, "\u2022")
    .replace(/Â/g, "");

const renderInlineText = (text, keyPrefix) => {
  const parts = normalizePolicyText(text).split(
    /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g,
  );
  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return (
        <a
          key={key}
          href={link[2]}
          className="text-[#E65316] underline underline-offset-2 hover:text-[#F01E1F]"
        >
          {link[1]}
        </a>
      );
    }
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const getHeadingText = (line) => line.replace(/^\*+|\*+$/g, "").trim();

const PolicyContent = ({ content }) => {
  const lines = normalizePolicyText(content).split("\n");
  const blocks = [];
  let listItems = [];

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push(
      <ul key={`list-${blocks.length}`} className="my-4 list-disc space-y-2 pl-6">
        {listItems.map((item, index) => (
          <li key={`item-${index}`}>{renderInlineText(item, `list-${index}`)}</li>
        ))}
      </ul>,
    );
    listItems = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const bullet = trimmed.match(/^(?:\u2022|[-*])\s+(.*)$/);
    const boldHeading = /^\*{2,}.+\*{2,}$/.test(trimmed);

    if (bullet) {
      listItems.push(bullet[1]);
      return;
    }

    flushList();
    if (!trimmed) return;

    if (boldHeading) {
      blocks.push(
        <h3
          key={`heading-${index}`}
          className={`mt-8 font-bold leading-snug text-[#111111] ${
            /^\*{2,}\d+\./.test(trimmed)
              ? "border-t border-[#111111]/10 pt-6 text-lg"
              : "text-base"
          }`}
        >
          {getHeadingText(trimmed)}
        </h3>,
      );
      return;
    }

    blocks.push(
      <p key={`paragraph-${index}`} className="my-3 text-[15px] leading-7 text-[#292624]">
        {renderInlineText(trimmed, `paragraph-${index}`)}
      </p>,
    );
  });

  flushList();
  return <div>{blocks}</div>;
};

export const PolicyModal = ({ isOpen, onClose, triggerRef }) => {
  const [activePolicy, setActivePolicy] = useState("privacy");
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const triggerElement = triggerRef.current;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.dispatchEvent(new CustomEvent("movementor:modal", { detail: true }));
    closeButtonRef.current?.focus();
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = "";
      window.dispatchEvent(new CustomEvent("movementor:modal", { detail: false }));
      window.removeEventListener("keydown", closeOnEscape);
      triggerElement?.focus();
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-[#111111]/80 p-4 sm:p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="flex h-[calc(100dvh-2rem)] max-h-[900px] w-full max-w-[1000px] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl sm:h-[min(85vh,900px)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="policy-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
      >
        <header className="shrink-0 border-b border-[#111111]/10 bg-white px-5 py-4 sm:px-8 sm:py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FE7815]">
                MoveMentor Education
              </p>
              <h2 id="policy-modal-title" className="mt-1 text-2xl font-bold text-[#111111]">
                Policies
              </h2>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Close policies"
              className="rounded-full p-2 text-[#111111]/70 transition-colors hover:bg-[#111111]/5 hover:text-[#111111] focus:outline-none focus:ring-2 focus:ring-[#FE7815]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-5 flex gap-2 overflow-x-auto" role="tablist" aria-label="Policy documents">
            {POLICY_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activePolicy === tab.id}
                onClick={() => setActivePolicy(tab.id)}
                className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#FE7815] ${
                  activePolicy === tab.id
                    ? "bg-gradient-to-r from-[#FE7815] to-[#F01E1F] text-white"
                    : "bg-[#111111]/5 text-[#111111]/70 hover:bg-[#FE7815]/10 hover:text-[#111111]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </header>
        <div
          className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain bg-[#FCFBF9] px-5 py-6 [-webkit-overflow-scrolling:touch] sm:px-10 sm:py-8"
          data-lenis-prevent
          role="tabpanel"
          onTouchStart={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
          onTouchEnd={(event) => event.stopPropagation()}
        >
          <div className="mx-auto max-w-3xl">
            <PolicyContent content={POLICY_SECTIONS[activePolicy]} />
          </div>
        </div>
      </div>
    </div>
  );
};
